/* eslint-disable @typescript-eslint/no-explicit-any */
import { NgClass, NgIf } from '@angular/common';
import { AfterContentChecked, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { BasicList } from '@app/models/base';
import { AllOpportunityFilters, LicenseTypeList, OppListItem, RegionList } from '@app/models/opportunities';
import { DatePickerPipe } from '@app/pipes/datePicker.pipe';
import { LoginService, ToastService } from '@app/services';
import { OpportunitiesService } from '@app/services/opportunities.service';
import { environment } from '@env/environment';
import { debounce } from '@helpers/debounce';
import { Subject, takeWhile } from 'rxjs';

export interface DialogData {
  licenseInfo: LicenseTypeList;
}

@Component({
  selector: 'qb-opportunities',
  templateUrl: './opportunities.component.html',
  styleUrls: ['./opportunities.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatButton,
    MatInput,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    NgClass,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    MatPaginator,
    MatProgressSpinner,
    DatePickerPipe,
    MatTooltipModule,
    NgIf,
    MatIconModule,
  ],
})
export class OpportunitiesComponent implements OnInit, OnDestroy, AfterContentChecked {
  @ViewChild('oppPaginator', { static: true }) opportunityPaginator: MatPaginator;
  @ViewChild('prequalAppPaginator', { static: true }) prequalAppPaginator: MatPaginator;

  constructor(
    private formBuilder: FormBuilder,
    private opportunitiesService: OpportunitiesService,
    private loginService: LoginService,
    private changeDetector: ChangeDetectorRef,
    private router: Router,
    private toast: ToastService
  ) {}

  /******** START - PUBLIC MEMBERS *******/
  opportunitiesForm: FormGroup;

  totalRows = 0;
  currentPage = 0;
  pageSize = 25;
  pageSizeOptions: number[] = [10, 25, 100];

  qbBellOffIcon = '../../assets/qb-bell-off.png';
  qbBellOnGreenIcon = '../../assets/qb-bell-on-green.png';

  loadingAllApps: boolean = true;
  selectedOpp: OppListItem = null;

  // data table
  displayedColumns: string[] = [
    'oppNumber',
    'agencyName',
    'oppName',
    'submissionCutOffDate',
    'bidDate',
    'licenseReqTtoPrequal',
    'jobWalks',
    'prequalStatus',
    'subscribe',
    'area',
    // 'status',
    // 'icon'
  ];

  public dataSource = new MatTableDataSource();

  opportunityDataSubject = new Subject<OppListItem[]>();

  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }

  private sortRef: MatSort;

  @ViewChild(MatSort) set sort(sort: MatSort) {
    this.sortRef = sort;
    if (this.dataSource && sort) {
      this.dataSource.sort = sort;
    }
  }

  filterOn = false;
  componentActive = true;
  oppRegions: RegionList[];
  oppLicenseTypes: LicenseTypeList[];
  oppAgencies: BasicList[];
  opportunityFilters: AllOpportunityFilters = {};

  debouncedGetOpportunities: () => void;

  get loggedInVendor() {
    return this.loginService.isVendor;
  }

  environment = environment;

  /******** END - PUBLIC MEMBERS *******/

  ngOnInit(): void {
    this.clearFilters(false);
    this.buildForm();
    this.loadOpportunityTableData();

    this.opportunityDataSubject.pipe(takeWhile(() => this.componentActive)).subscribe(data => {
      this.dataSource = new MatTableDataSource<OppListItem>(data);

      this.dataSource.sortingDataAccessor = (item: any, property: string) => {
        if (property === 'prequalDue' || property === 'responsibleBiddersDate') {
          return item[property] ? new Date(item[property]).getTime() : 0;
        }
        return typeof item[property] === 'string' ? item[property].toLowerCase() : item[property];
      };
      if (this.sortRef) {
        this.dataSource.sort = this.sortRef;
      }
    });

    this.debouncedGetOpportunities = debounce(
      () => {
        this.getOpportunities();
      },
      200,
      true
    );
  }

  ngAfterContentChecked() {
    this.changeDetector.detectChanges();
  }

  ngOnDestroy(): void {
    this.componentActive = false;
  }

  /******** START - PRIVATE FUNCTIONS *******/

  private buildForm(): void {
    const regionValue: number = 0,
      licenseTypeValue: number = 0,
      agencyValue: number = 0,
      searchValue: string = '';
    this.opportunitiesForm = this.formBuilder.group({
      region: [regionValue],
      licenseType: [licenseTypeValue],
      agency: [agencyValue],
      searchQuery: [searchValue],
    });

    this.opportunitiesForm.valueChanges.pipe(takeWhile(() => this.componentActive)).subscribe(v => {
      this.filterOn = !(v.region == 0 && v.licenseType == 0 && v.agency == 0 && v.searchQuery == '');
    });
  }

  private getOpportunities() {
    this.opportunitiesService.getOpportunities(this.opportunityFilters).subscribe(oppData => {
      this.opportunityDataSubject.next(oppData);
      this.loadingAllApps = false;
    });
  }

  /******** END - PRIVATE FUNCTIONS *******/

  /******** START - PUBLIC FUNCTIONS *******/
  convertToLowerCase(oppName: string): string {
    const convertedValue: string = oppName.toUpperCase();
    return convertedValue;
  }

  loadOpportunityTableData(): void {
    this.opportunitiesService.getOpportunityTableData().subscribe(opportunityTableDataResponse => {
      this.loadingAllApps = false;
      this.opportunityDataSubject.next(opportunityTableDataResponse.opportunitiesList);

      this.oppRegions = opportunityTableDataResponse.regionList;
      this.oppLicenseTypes = opportunityTableDataResponse.licenseTypeList;
      this.oppAgencies = opportunityTableDataResponse.agencyList;
    });
  }

  // Region
  filterByRegion(regionEvent: MatSelectChange): void {
    this.loadingAllApps = true;
    this.opportunityFilters.regionId = regionEvent.value;
    this.getOpportunities();
  }

  // License Type
  filterByLicenseType(licenseTypeEvent: MatSelectChange): void {
    this.loadingAllApps = true;
    this.opportunityFilters.licenseId = licenseTypeEvent.value;
    this.getOpportunities();
  }

  // Agency
  filterByAgency(agencyEvent: MatSelectChange): void {
    this.loadingAllApps = true;
    this.opportunityFilters.agencyId = agencyEvent.value;
    this.getOpportunities();
  }

  // Search
  searchOpportunity(): void {
    this.loadingAllApps = true;
    // Eventually debounce this and call on (keyup)
    this.opportunityFilters.searchQuery = this.opportunitiesForm.controls['searchQuery'].value;

    this.debouncedGetOpportunities();
  }

  clearFilters(makeCall = true): void {
    this.loadingAllApps = true;
    this.opportunityFilters = {
      regionId: 0,
      licenseId: 0,
      agencyId: 0,
      searchQuery: '',
    };
    this.filterOn = false;

    if (makeCall) {
      this.getOpportunities();
    }
  }

  changeSubscription(opp: OppListItem, type: string) {
    const oppAgencyToken = opp.agencyToken;
    const oppEventToken = opp.eventToken;

    if (type === 'subscribed') {
      this.opportunitiesService
        .updateSubscribeToOpp(oppAgencyToken, oppEventToken)
        .pipe(takeWhile(() => this.componentActive))
        .subscribe({
          next: () => {
            opp.userSubscribed = true;
            this.toast.info('Subscribed to this Opportunity');
          },
          error: () => {
            this.toast.error('Error Subscribing to this Opportunity');
          },
        });
    } else if (type === 'unsubscribed') {
      this.opportunitiesService
        .updateUnsubscribeFromOpp(oppAgencyToken, oppEventToken)
        .pipe(takeWhile(() => this.componentActive))
        .subscribe({
          next: () => {
            opp.userSubscribed = false;
            this.toast.info('Unsubscribed from this Opportunity');
          },
          error: () => {
            this.toast.error('Error Unsubscribing from this Opportunity');
          },
        });
    }
  }

  onSelectedPrequalStatus(opp: OppListItem) {
    const appListRoute = opp.isCUPCCAA ? '/vendor/applications/cupccaa' : '/vendor/applications/prequalification';

    // No matching app yet — send vendor to create/list for the required questionnaire type.
    if (opp.prequalStatus === 'Get Prequalified') {
      this.router.navigate([appListRoute]);
      return;
    }

    // In Progress / Approved / Rated: open the matching application in a new window when we have an id.
    if (
      opp.appId &&
      (opp.prequalStatus === 'In Progress' || opp.prequalStatus === 'Approved' || opp.prequalStatus === 'Rated')
    ) {
      const appUrl = this.router.serializeUrl(this.router.createUrlTree([appListRoute, opp.appId]));
      window.open(appUrl, '_blank');
    }
  }

  onSelectOpp(opp: OppListItem) {
    const oppAgencyToken = opp.agencyToken;
    const oppEventToken = opp.eventToken;

    const redirectUrl = environment.sbUrl + '/o/' + oppAgencyToken + '/' + oppEventToken;

    window.open(redirectUrl);
  }

  /******** END - PUBLIC FUNCTIONS *******/
}
